import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  TextField,
  Grid,
  Paper,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Checkbox,
  ListItemText,
  Radio,
  RadioGroup,
  FormControlLabel,
  Avatar,
  Box,
  FormHelperText,
  Typography,
  Tooltip,
  IconButton,
  InputAdornment,
  Divider,
  Card,
  CardContent,
} from "@material-ui/core";
import { 
  PhotoCamera, 
  CheckCircle, 
  Cancel, 
  Edit, 
  Lock, 
  Person, 
  Work, 
  Settings,
  Link as LinkIcon,
  Error as ErrorIcon,
  Save as SaveIcon,
  School as SchoolIcon,
} from "@material-ui/icons";
import { LoadingButton } from "@mui/lab";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Badge,
} from "@mui/material";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

// Custom components imports
import MainContainer from "../../components/MainContainer";
import Phone from "../tools/Phone";
import CropDialog from "../my-account/AccountConfiguration/CropDialog";

// Services imports
import PersonnelService from "../../services/PersonnelService";
import UserService from "../../services/UserService";
import DirectionService from "../../services/DirectionService";
import PlanningService from "../../services/PlanningService";
import FonctionService from "../../services/FonctionService";
import FundingService from "../../services/FundingService";

// Utils imports
import { hasPrivilege } from "../../utils/privilege";
import { headerDataAdmin } from "../../data/modules/admin-header";
import { userDetailContext } from "../../App";
import { useParams, useNavigate } from "react-router-dom";

// Constants
const PHONE_NUMBER_LENGTHS = [10, 13];
const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[A-Za-z]+$/;

const RequiredStar = () => (
  <Box
    component="span"
    sx={{ display: 'inline-block', mx: '5px', transform: 'scale(0.8)', color: 'red', fontSize: '20px' }}
  >
    *
  </Box>
);

const stageOption = [
  { value: "Sans indemnité", label: "Sans indemnité", indemnity: "0", level: "Aucun" },
  { value: "Bacc +2", label: "Bacc +2", indemnity: "150000", level: "Licence 1" },
  { value: "Bacc +3", label: "Bacc +3", indemnity: "200000", level: "Licence" },
  { value: "Bacc +4", label: "Bacc +4", indemnity: "250000", level: "Master 1" },
  { value: "Bacc +5", label: "Bacc +5", indemnity: "250000", level: "Master" }
];

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: '0 2px 12px 0 rgba(0, 0, 0, 0.1)',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    gap: theme.spacing(2),
  },
  loadingText: {
    color: theme.palette.text.secondary,
    fontSize: '1rem',
    marginTop: theme.spacing(1),
  },
  typeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    padding: theme.spacing(1, 3),
    borderRadius: theme.spacing(3),
    marginBottom: theme.spacing(3),
    width: 'fit-content',
    '& svg': {
      fontSize: '1.2rem',
    },
    '& span': {
      fontWeight: 600,
      fontSize: '1rem',
    },
  },
  fieldset: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    '& legend': {
      padding: theme.spacing(0, 2),
      fontWeight: 600,
      color: theme.palette.primary.main,
      fontSize: '1.1rem',
    },
  },
  formRow: {
    marginBottom: theme.spacing(2),
  },
  labelContainer: {
    paddingTop: theme.spacing(1.5),
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  disabledLabel: {
    paddingTop: theme.spacing(1.5),
    fontWeight: 500,
    color: theme.palette.text.disabled,
  },
  avatarContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  large: {
    width: theme.spacing(18),
    height: theme.spacing(18),
    cursor: 'pointer',
    transition: 'transform 0.2s',
    '&:hover': {
      transform: 'scale(1.05)',
    },
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  infoText: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    textAlign: 'center',
  },
  disabledField: {
    backgroundColor: '#f5f5f5',
    '& .MuiInputBase-root': {
      backgroundColor: '#f5f5f5',
    },
  },
  editIcon: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    color: theme.palette.warning.main,
  },
  lockIcon: {
    marginLeft: theme.spacing(1),
    fontSize: '1rem',
    color: theme.palette.grey[500],
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  editModeBadge: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2),
    fontSize: '0.75rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  updateOptionsCard: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(2),
  },
  updateOption: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    cursor: 'pointer',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  updateOptionSelected: {
    backgroundColor: theme.palette.primary.light + '20',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  updateOptionIcon: {
    fontSize: '2rem',
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  updateOptionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  updateOptionDescription: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  sectionDisabled: {
    opacity: 0.6,
    pointerEvents: 'none',
  },
  linkedSection: {
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -16,
      top: 0,
      bottom: 0,
      width: 4,
      backgroundColor: theme.palette.info.main,
      borderRadius: 2,
    },
  },
  successDialog: {
    '& .MuiDialog-paper': {
      borderRadius: theme.spacing(2),
      padding: theme.spacing(2),
    },
  },
  successIcon: {
    fontSize: '4rem',
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    fontSize: '4rem',
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  dialogButton: {
    marginTop: theme.spacing(2),
  },
}));

// Custom hook for form validation
const useFormValidation = (form, updateOptions, dataState, isEditing) => {
  const [errors, setErrors] = useState({});

  const validate = useCallback(() => {
    const newErrors = {};

    if (!isEditing) {
      // Mode création - tous les champs obligatoires
      if (!form.last_name?.trim()) newErrors.last_name = "Le nom est obligatoire";
      if (!form.telephone?.trim()) {
        newErrors.telephone = "Le téléphone est obligatoire";
      } else if (!PHONE_NUMBER_LENGTHS.includes(form.telephone.length)) {
        newErrors.telephone = "Numéro de téléphone invalide";
      }
      if (!form.email?.trim()) {
        newErrors.email = "L'email est obligatoire";
      } else if (!EMAIL_REGEX.test(form.email)) {
        newErrors.email = "Email invalide";
      }
      if (!form.sex) newErrors.sex = "Le sexe est obligatoire";
      if (!form.group?.length) newErrors.group = "Au moins un groupe est requis";
      if (!dataState.idservice) newErrors.service = "Le service est obligatoire";
      if (!dataState.matricule?.trim()) newErrors.matricule = "Le matricule est obligatoire";
      if (!form.date_prise) newErrors.date_prise = "La date de prise est obligatoire";
      if (!form.date_fin) newErrors.date_fin = "La date de fin est obligatoire";
    } else {
      // Mode édition
      if (updateOptions.personalInfo) {
        if (!form.last_name?.trim()) newErrors.last_name = "Le nom est obligatoire";
        if (!form.telephone?.trim()) {
          newErrors.telephone = "Le téléphone est obligatoire";
        } else if (!PHONE_NUMBER_LENGTHS.includes(form.telephone.length)) {
          newErrors.telephone = "Numéro de téléphone invalide";
        }
        if (!form.email?.trim()) {
          newErrors.email = "L'email est obligatoire";
        } else if (!EMAIL_REGEX.test(form.email)) {
          newErrors.email = "Email invalide";
        }
        if (!form.sex) newErrors.sex = "Le sexe est obligatoire";
        if (!form.group?.length) newErrors.group = "Au moins un groupe est requis";
      }

      if (updateOptions.professionalAndContract) {
        if (!dataState.idservice) newErrors.service = "Le service est obligatoire";
        if (!dataState.matricule?.trim()) newErrors.matricule = "Le matricule est obligatoire";
        if (!form.date_prise) newErrors.date_prise = "La date de prise est obligatoire";
        if (!form.date_fin) newErrors.date_fin = "La date de fin est obligatoire";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form, updateOptions, dataState, isEditing]);

  return { errors, validate, setErrors };
};

function StagiaireAdd() {
  const classes = useStyles();
  const navigate = useNavigate();
  const { id: idUser } = useParams();
  const contextData = React.useContext(userDetailContext);
  const isEditing = !!idUser;
  
  // État pour les numéros max
  const [maxNumbers, setMaxNumbers] = useState({
    maxstg: 0,
    isLoading: true
  });

  // État pour le chargement initial des données
  const [initialLoading, setInitialLoading] = useState(true);

  const [updateOptions, setUpdateOptions] = useState({
    personalInfo: true,
    professionalAndContract: false,
  });

  const [form, setForm] = useState({
    id: null,
    first_name: "",
    last_name: "",
    telephone: "",
    sex: "",
    email: "",
    password: "",
    group: [],
    name_image: "",
    url_name_image: "",
    date_notif: "",
    date_prise: "",
    date_fin: "",
    typestage: "",
    indemnity: "0",
  });

  const [uiState, setUiState] = useState({
    loading: false,
    successDialogOpen: false,
    errorDialogOpen: false,
    errorMessage: "",
    successMessage: "",
    opens: false,
    cropping: false,
    newImage: null,
    tempImage: null,
    selectedFile: null,
    croppedFileName: "",
    file: null,
    picture: null,
  });

  const [dataState, setDataState] = useState({
    directions: [],
    services: [],
    users: [],
    functionList: [],
    fundingList: [],
    groups: [],
    sexes: [],
    iddir: 0,
    idservice: 0,
    idUserSup: 0,
    matricule: "",
    functionId: null,
    fundingId: null,
    originalData: null,
  });

  const fileInputRef = useRef(null);
  const { errors, validate, setErrors } = useFormValidation(form, updateOptions, dataState, isEditing);

  // Fonction pour générer le matricule pour stagiaire
  const generateRegistrationNumber = useCallback((maxStg) => {
    const currentYear = new Date().getFullYear();
    const lastTwoDigits = currentYear % 100;
    const nextstgNumber = String(parseInt(maxStg, 10) + 1).padStart(3, '0');
    return `STG/${nextstgNumber}/${lastTwoDigits}`;
  }, []);

  const capitalizeString = useCallback((str) => {
    if (!str) return "";
    return str.replace(/\b\w/g, char => char.toUpperCase());
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    if (name === 'first_name') {
      setForm(prev => ({ ...prev, [name]: capitalizeString(value) }));
    } else if (name === 'telephone') {
      const cleaned = value.replace(/[^0-9\s]/g, '');
      setForm(prev => ({ ...prev, [name]: cleaned }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  }, [capitalizeString, errors, setErrors]);

  const handleStageTypeChange = useCallback((e) => {
    const value = e.target.value;
    const selectedOption = stageOption.find(option => option.value === value);
    setForm(prev => ({
      ...prev,
      typestage: value,
      indemnity: selectedOption ? selectedOption.indemnity : "0",
    }));
  }, []);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;

    const originalName = file.name || "image.jpg";
    const extensionMatch = originalName.match(/\.[^/.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : ".jpg";
    const firstName = form.first_name?.trim();
    const croppedFileName = firstName
      ? `${firstName.replace(/\s+/g, "_")}${extension}`
      : originalName;

    const reader = new FileReader();
    reader.onloadend = () => {
      setUiState(prev => ({
        ...prev,
        tempImage: reader.result,
        croppedFileName,
        cropping: true,
      }));
    };
    reader.readAsDataURL(file);
  }, [form.first_name]);

  const handleSaveImage = useCallback(async () => {
    const { selectedFile } = uiState;
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("photos", selectedFile);
    formData.append("name_image", uiState.croppedFileName );
    
    try {
      const uploadedFileName = await PersonnelService.importphotos(formData);
      setUiState(prev => ({
        ...prev,
        opens: false,
        file: URL.createObjectURL(selectedFile),
        picture: selectedFile,
      }));
      setForm(prev => ({ ...prev, name_image: uploadedFileName || selectedFile.name }));
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  }, [uiState]);

  const handleCloseSuccessDialog = () => {
    setUiState(prev => ({ ...prev, successDialogOpen: false }));
    navigate("/stglist");
  };

  const handleCloseErrorDialog = () => {
    setUiState(prev => ({ ...prev, errorDialogOpen: false }));
  };

  const handleSubmit = useCallback(async () => {
    if (!validate()) {
      setUiState(prev => ({
        ...prev,
        errorDialogOpen: true,
        errorMessage: "Veuillez corriger les erreurs dans le formulaire",
      }));
      return;
    }

    setUiState(prev => ({ ...prev, loading: true }));

    try {
      let response;
      
      if (isEditing) {
        // Mode édition - utilisation de updateUserPartial
        const updateData = {
          id: form.id,
          updateOptions: updateOptions,
        };

        if (updateOptions.personalInfo) {
          updateData.personalInfo = {
            first_name: form.first_name,
            last_name: form.last_name,
            telephone: form.telephone,
            sex: form.sex,
            email: form.email,
            password: form.password,
            group: form.group,
            name_image: form.name_image,
          };
        }

        if (updateOptions.professionalAndContract) {
          updateData.professionalInfo = {
            service_id: dataState.idservice,
            superior_id: dataState.idUserSup,
            registration_number: dataState.matricule,
            function_id: dataState.functionId?.function_id,
            funding_id: dataState.fundingId?.funding_id,
          };
          
          updateData.contractInfo = {
            date_notif: form.date_notif,
            date_prise: form.date_prise,
            date_fin: form.date_fin,
            type: "stagiaire",
            typestage: form.typestage,
            indemnity: form.indemnity,
          };
        }

        response = await UserService.updateUserPartial(updateData);
      } else {
        // Mode création - utilisation de createUser
        const userData = {
          first_name: form.first_name,
          last_name: form.last_name,
          telephone: form.telephone,
          sex: form.sex,
          email: form.email,
          password: form.password,
          group: form.group,
          name_image: form.name_image,
          service_id: dataState.idservice,
          superior_id: dataState.idUserSup,
          registration_number: dataState.matricule,
          function_id: dataState.functionId?.function_id,
          funding_id: dataState.fundingId?.funding_id,
          date_notif: form.date_notif,
          date_prise: form.date_prise,
          date_fin: form.date_fin,
          type: "stagiaire",
          typestage: form.typestage,
          indemnity: form.indemnity,
        };
        
        response = await UserService.createUser(userData);
      }

      setUiState(prev => ({ ...prev, loading: false }));

      if (response && response.status) {
        setUiState(prev => ({
          ...prev,
          successDialogOpen: true,
          successMessage: isEditing ? "Stagiaire mis à jour avec succès !" : "Stagiaire créé avec succès !",
        }));
      } else {
        setUiState(prev => ({
          ...prev,
          errorDialogOpen: true,
          errorMessage: response?.message || "Une erreur s'est produite",
        }));
      }
    } catch (error) {
      console.error("Submit error:", error);
      setUiState(prev => ({
        ...prev,
        loading: false,
        errorDialogOpen: true,
        errorMessage: "Une erreur s'est produite. Veuillez réessayer",
      }));
    }
  }, [form, dataState, updateOptions, validate, isEditing]);

  // Data fetching effects
  useEffect(() => {
    const fetchInitialData = async () => {
      setInitialLoading(true);
      try {
        // Récupérer toutes les données en parallèle
        const [directions, sexes, groups, functions, fundings, matriculeData] = await Promise.all([
          DirectionService.getDirection(),
          UserService.getSex(),
          UserService.getAllGroup(),
          FonctionService.getfonction(),
          FundingService.getFunding(),
          UserService.maxmatricule()
        ]);

        // Extraire les valeurs max pour stagiaire
        const maxStg = matriculeData?.datastg || 0;
        
        // Mettre à jour l'état des max
        setMaxNumbers({
          maxstg: maxStg,
          isLoading: false
        });

        setDataState(prev => ({
          ...prev,
          directions: directions?.data || [],
          sexes: sexes?.data?.map(s => ({ sex_id: s.sex_id, name: s.name })) || [],
          groups: groups?.data?.map(d => ({ group_id: d.group_id, name: d.name, desc: d.desc })) || [],
          functionList: functions?.data || [],
          fundingList: fundings?.data || [],
        }));

        // Générer le matricule initial APRÈS avoir les valeurs max
        const initialMatricule = generateRegistrationNumber(maxStg);

        setDataState(prev => ({ 
          ...prev, 
          matricule: initialMatricule 
        }));

      } catch (error) {
        console.error("Error fetching initial data:", error);
        setMaxNumbers(prev => ({ ...prev, isLoading: false }));
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, [generateRegistrationNumber]);

  // Effet pour mettre à jour le matricule quand maxNumbers change
  useEffect(() => {
    if (!maxNumbers.isLoading && !isEditing) {
      const newMatricule = generateRegistrationNumber(maxNumbers.maxstg);
      setDataState(prev => ({ ...prev, matricule: newMatricule }));
    }
  }, [maxNumbers.maxstg, maxNumbers.isLoading, isEditing, generateRegistrationNumber]);

  useEffect(() => {
    if (!idUser) return;

    const fetchUserData = async () => {
      setInitialLoading(true);
      try {
        const [userInfo, userSummary, userSuperior] = await Promise.all([
          UserService.getUserId(idUser),
          UserService.getUserSummaryByUserId(idUser),
          UserService.getUserSuperiorByUserId(idUser),
        ]);

        if (userInfo?.data) {
          const userData = {
            id: idUser,
            first_name: userInfo.data.person.first_name,
            last_name: userInfo.data.person.last_name,
            telephone: userInfo.data.phone_number,
            email: userInfo.data.email,
            sex: userInfo.data.person.sex_id,
            password: userInfo.data.password,
            group: userInfo.data.group.map(pv => pv.group_id),
            name_image: userInfo.data.name_image,
            url_name_image: userInfo.data._url_name_image,
            date_notif: userInfo.data.date_notif || "",
            date_prise: userInfo.data.date_prise || "",
            date_fin: userInfo.data.date_fin || "",
            typestage: userInfo.data.typestage || "",
            indemnity: userInfo.data.indemnity || "0",
          };
          setForm(prev => ({ ...prev, ...userData }));
          setDataState(prev => ({ ...prev, originalData: userData }));
        }

        if (userSummary?.status) {
          setDataState(prev => ({
            ...prev,
            iddir: userSummary.data.direction_id,
            matricule: userSummary.data.registration_number,
            functionId: {
              name: userSummary.data.function_name,
              function_id: userSummary.data.function_id,
            },
            fundingId: userSummary.data.funding_id,
          }));

          const services = await PlanningService.Direction(userSummary.data.direction_id);
          if (services?.data) {
            setDataState(prev => ({
              ...prev,
              services: services.data,
              idservice: userSummary.data.service_id,
            }));
          }
        }

        if (userSuperior?.status && userSuperior.data.superior_id) {
          setDataState(prev => ({
            ...prev,
            idUserSup: userSuperior.data.superior_id,
          }));
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setInitialLoading(false);
      }
    };

    fetchUserData();
  }, [idUser]);

  const handleDirectionChange = useCallback(async (event) => {
    const directionId = event.target.value;
    setDataState(prev => ({ ...prev, iddir: directionId }));
    
    try {
      const services = await PlanningService.Direction(directionId);
      if (services?.data) {
        setDataState(prev => ({ ...prev, services: services.data }));
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    }
  }, []);

  const handleServiceChange = useCallback((event) => {
    const serviceId = event.target.value;
    setDataState(prev => ({ ...prev, idservice: serviceId }));
  }, []);

  const getGroupLabel = useCallback((groupId) => {
    const group = dataState.groups.find(g => g.group_id === groupId);
    return group?.name || "";
  }, [dataState.groups]);

  const FormRow = useCallback(({ label, required, children, error, disabled = false }) => (
    <Grid container spacing={2} className={classes.formRow}>
      <Grid item xs={12} md={3}>
        <div className={disabled ? classes.disabledLabel : classes.labelContainer}>
          {label}
          {required && <RequiredStar />}
          {disabled && <Lock className={classes.lockIcon} />}
        </div>
      </Grid>
      <Grid item xs={12} md={8}>
        <div className={disabled ? classes.disabledField : ''}>
          {children}
        </div>
        {error && <FormHelperText error>{error}</FormHelperText>}
      </Grid>
    </Grid>
  ), [classes]);

  const FormSection = useCallback(({ title, children, sectionKey, linked = false }) => {
    const isDisabled = isEditing && (
      sectionKey === 'professionalInfo' || sectionKey === 'contractInfo' 
        ? !updateOptions.professionalAndContract
        : !updateOptions[sectionKey]
    );
    
    return (
      <fieldset className={`${classes.fieldset} ${linked && !isDisabled ? classes.linkedSection : ''}`}>
        <div className={classes.sectionHeader}>
          <legend>{title}</legend>
          {isEditing && (
            <div className={classes.editModeBadge} style={{ 
              backgroundColor: isDisabled ? '#f5f5f5' : (linked ? '#e3f2fd' : '#e8f5e9'),
              color: isDisabled ? '#9e9e9e' : (linked ? '#1976d2' : '#2e7d32')
            }}>
              {isDisabled ? (
                <>
                  <Lock className={classes.editIcon} />
                  <span>Non modifiable</span>
                </>
              ) : (
                <>
                  {linked && <LinkIcon style={{ fontSize: '1rem' }} />}
                  <Edit className={classes.editIcon} />
                  <span>Modifiable</span>
                </>
              )}
            </div>
          )}
        </div>
        <div className={isDisabled ? classes.sectionDisabled : ''}>
          {children}
        </div>
      </fieldset>
    );
  }, [classes, isEditing, updateOptions]);

  const UpdateOptionsSelector = () => {
    if (!isEditing) return null;

    const options = [
      {
        key: 'personalInfo',
        title: 'Informations personnelles',
        description: 'Nom, prénom, téléphone, email, groupes, photo',
        icon: <Person className={classes.updateOptionIcon} />,
      },
      {
        key: 'professionalAndContract',
        title: 'Informations professionnelles et contractuelles (liées)',
        description: 'Direction, service, poste, financement, matricule, dates de contrat, niveau de stage, indemnité',
        icon: <Work className={classes.updateOptionIcon} />,
        note: '⚠️ Ces deux sections sont liées et doivent être modifiées ensemble',
      },
    ];

    const handleOptionClick = (key) => {
      if (key === 'professionalAndContract') {
        setUpdateOptions(prev => ({
          ...prev,
          professionalAndContract: !prev.professionalAndContract,
        }));
      } else {
        setUpdateOptions(prev => ({
          ...prev,
          [key]: !prev[key],
        }));
      }
    };

    return (
      <Card className={classes.updateOptionsCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Settings />
            Sélectionnez les sections à modifier
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Choisissez les informations que vous souhaitez mettre à jour pour ce stagiaire
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Grid container spacing={2}>
            {options.map((option) => (
              <Grid item xs={12} md={6} key={option.key}>
                <div 
                  className={`${classes.updateOption} ${updateOptions[option.key] ? classes.updateOptionSelected : ''}`}
                  onClick={() => handleOptionClick(option.key)}
                >
                  {option.icon}
                  <div style={{ flex: 1 }}>
                    <div className={classes.updateOptionTitle}>
                      <Checkbox 
                        checked={updateOptions[option.key]} 
                        size="small"
                        style={{ padding: 0, marginRight: 8 }}
                      />
                      {option.title}
                    </div>
                    <div className={classes.updateOptionDescription}>
                      {option.description}
                    </div>
                    {option.note && (
                      <Typography variant="caption" color="error" style={{ marginTop: 8, display: 'block' }}>
                        {option.note}
                      </Typography>
                    )}
                  </div>
                </div>
              </Grid>
            ))}
          </Grid>
          
          {updateOptions.professionalAndContract && (
            <div style={{ 
              marginTop: 16, 
              padding: 12, 
              backgroundColor: '#e3f2fd', 
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <LinkIcon style={{ color: '#1976d2' }} />
              <Typography variant="body2" style={{ color: '#1976d2' }}>
                Les sections "Informations professionnelles" et "Informations contractuelles" seront modifiées ensemble
              </Typography>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Afficher un écran de chargement pendant la récupération des données initiales
  if (initialLoading) {
    return (
      <MainContainer {...headerDataAdmin}>
        <div className={classes.root}>
          <Paper className={classes.paper}>
            <div className={classes.loadingContainer}>
              <CircularProgress size={60} thickness={4} />
              <Typography variant="h6" className={classes.loadingText}>
                Veuillez patienter...
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Chargement des données en cours
              </Typography>
            </div>
          </Paper>
        </div>
      </MainContainer>
    );
  }

  const isProfessionalDisabled = isEditing && !updateOptions.professionalAndContract;
  const isContractDisabled = isEditing && !updateOptions.professionalAndContract;

  return (
    <MainContainer {...headerDataAdmin}>
      <div className={classes.root}>
        {/* Badge Type Stagiaire */}
        <div className={classes.typeBadge}>
          <SchoolIcon />
          <span>Type : Stagiaire</span>
        </div>

        {/* Dialog de succès */}
        <Dialog open={uiState.successDialogOpen} onClose={handleCloseSuccessDialog} className={classes.successDialog}>
          <DialogContent style={{ textAlign: 'center', padding: '32px' }}>
            <CheckCircle className={classes.successIcon} />
            <Typography variant="h5" gutterBottom style={{ fontWeight: 600 }}>Succès !</Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>{uiState.successMessage}</Typography>
            <Button variant="contained" color="primary" onClick={handleCloseSuccessDialog} className={classes.dialogButton} startIcon={<CheckCircle />}>OK</Button>
          </DialogContent>
        </Dialog>

        {/* Dialog d'erreur */}
        <Dialog open={uiState.errorDialogOpen} onClose={handleCloseErrorDialog} className={classes.successDialog}>
          <DialogContent style={{ textAlign: 'center', padding: '32px' }}>
            <ErrorIcon className={classes.errorIcon} />
            <Typography variant="h5" gutterBottom style={{ fontWeight: 600 }}>Erreur</Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>{uiState.errorMessage}</Typography>
            <Button variant="contained" color="primary" onClick={handleCloseErrorDialog} className={classes.dialogButton} startIcon={<Cancel />}>Fermer</Button>
          </DialogContent>
        </Dialog>

        {/* Action Buttons */}
        <div className={classes.buttonGroup}>
          {hasPrivilege(contextData?.privileges, 'Admin-User-List') && (
            <LoadingButton variant="contained" color="primary" onClick={handleSubmit} loading={uiState.loading} loadingPosition="center" startIcon={<SaveIcon />}>
              {isEditing ? "METTRE À JOUR" : "ENREGISTRER"}
            </LoadingButton>
          )}
          <Button variant="outlined" onClick={() => navigate("/stglist")} startIcon={<Cancel />}>ANNULER</Button>
        </div>

        <UpdateOptionsSelector />

        <Paper className={classes.paper} elevation={0}>
          <Grid container spacing={3}>
            {/* Personal Information Section */}
            <Grid item xs={12}>
              <FormSection title="Informations personnelles" sectionKey="personalInfo">
                <Grid container spacing={2}>
                  <Grid item xs={12} md={9}>
                    <FormRow label="Nom" required error={errors.last_name} disabled={isEditing && !updateOptions.personalInfo}>
                      <TextField 
                        fullWidth 
                        size="small" 
                        placeholder="Nom" 
                        variant="outlined" 
                        value={form.last_name} 
                        onChange={handleInputChange} 
                        name="last_name" 
                        inputProps={{ style: { textTransform: "uppercase" } }} 
                        disabled={isEditing && !updateOptions.personalInfo} 
                      />
                    </FormRow>

                    <FormRow label="Prénom" disabled={isEditing && !updateOptions.personalInfo}>
                      <TextField 
                        fullWidth 
                        size="small" 
                        placeholder="Prénom" 
                        variant="outlined" 
                        value={form.first_name} 
                        onChange={handleInputChange} 
                        name="first_name" 
                        disabled={isEditing && !updateOptions.personalInfo} 
                      />
                    </FormRow>

                    <FormRow label="Téléphone" required error={errors.telephone} disabled={isEditing && !updateOptions.personalInfo}>
                      <Phone name="telephone" value={form.telephone} onChange={handleInputChange} />
                    </FormRow>

                    <FormRow label="Sexe" required error={errors.sex} disabled={isEditing && !updateOptions.personalInfo}>
                      <RadioGroup row name="sex" value={form.sex} onChange={handleInputChange}>
                        {dataState.sexes.map(sex => (
                          <FormControlLabel key={sex.sex_id} value={sex.sex_id} control={<Radio />} label={sex.name} />
                        ))}
                      </RadioGroup>
                    </FormRow>

                    <FormRow label="Email" required error={errors.email} disabled={isEditing && !updateOptions.personalInfo}>
                      <TextField 
                        fullWidth 
                        size="small" 
                        placeholder="email@exemple.com" 
                        variant="outlined" 
                        type="email" 
                        value={form.email} 
                        onChange={handleInputChange} 
                        name="email" 
                        disabled={isEditing && !updateOptions.personalInfo} 
                      />
                    </FormRow>

                    <FormRow label="Groupes" required error={errors.group} disabled={isEditing && !updateOptions.personalInfo}>
                      <FormControl fullWidth variant="outlined" size="small" disabled={isEditing && !updateOptions.personalInfo}>
                        <InputLabel>Groupes</InputLabel>
                        <Select 
                          multiple 
                          value={form.group} 
                          onChange={handleInputChange} 
                          name="group" 
                          label="Groupes" 
                          renderValue={(selected) => (
                            <div className={classes.chips}>
                              {selected.map(value => (
                                <Chip key={value} label={getGroupLabel(value)} className={classes.chip} size="small" />
                              ))}
                            </div>
                          )}
                        >
                          {dataState.groups.map(group => (
                            <MenuItem key={group.group_id} value={group.group_id}>
                              <Checkbox checked={form.group.includes(group.group_id)} />
                              <ListItemText primary={group.name} secondary={group.desc} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </FormRow>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <div className={classes.avatarContainer}>
                      <Badge 
                        overlap="circular" 
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} 
                        badgeContent={
                          <Tooltip title="Modifier la photo">
                            <IconButton 
                              size="small" 
                              onClick={() => setUiState(prev => ({ ...prev, opens: true }))} 
                              style={{ backgroundColor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} 
                              disabled={isEditing && !updateOptions.personalInfo}
                            >
                              <PhotoCamera fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        }
                      >
                        <Avatar src={uiState.file || form.url_name_image} className={classes.large} />
                      </Badge>
                    </div>
                  </Grid>
                </Grid>
              </FormSection>
            </Grid>

            {/* Professional Information Section */}
            <Grid item xs={12}>
              <FormSection title="Informations professionnelles" sectionKey="professionalInfo" linked={isEditing && updateOptions.professionalAndContract}>
                <FormRow label="Direction" disabled={isProfessionalDisabled}>
                  <FormControl fullWidth variant="outlined" size="small" disabled={isProfessionalDisabled}>
                    <InputLabel>Direction</InputLabel>
                    <Select value={dataState.iddir} onChange={handleDirectionChange} label="Direction" disabled={isProfessionalDisabled}>
                      {dataState.directions.map(dir => (
                        <MenuItem key={dir.direction_id} value={dir.direction_id}>{dir.code}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FormRow>

                <FormRow label="Service" disabled={isProfessionalDisabled}>
                  <FormControl fullWidth variant="outlined" size="small" disabled={isProfessionalDisabled}>
                    <InputLabel>Service</InputLabel>
                    <Select value={dataState.idservice} onChange={handleServiceChange} label="Service" disabled={isProfessionalDisabled}>
                      {dataState.services.map(service => (
                        <MenuItem key={service.service_id} value={service.service_id}>{service.code}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FormRow>

                <FormRow label="Poste" disabled={isProfessionalDisabled}>
                  <Autocomplete 
                    options={dataState.functionList} 
                    value={dataState.functionId} 
                    onChange={(_, value) => setDataState(prev => ({ ...prev, functionId: value }))} 
                    getOptionLabel={(option) => option?.name || ""} 
                    disabled={isProfessionalDisabled} 
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        size="small" 
                        placeholder="Sélectionner un poste" 
                        variant="outlined" 
                        disabled={isProfessionalDisabled} 
                      />
                    )} 
                  />
                </FormRow>

                <FormRow label="Financement" disabled={isProfessionalDisabled}>
                  <Autocomplete 
                    options={dataState.fundingList} 
                    value={dataState.fundingId} 
                    onChange={(_, value) => setDataState(prev => ({ ...prev, fundingId: value }))} 
                    getOptionLabel={(option) => option?.code || ""} 
                    disabled={isProfessionalDisabled} 
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        size="small" 
                        placeholder="Sélectionner un financement" 
                        variant="outlined" 
                        disabled={isProfessionalDisabled} 
                      />
                    )} 
                  />
                </FormRow>

                <FormRow label="Matricule" required error={errors.matricule} disabled>
                  <TextField 
                    fullWidth 
                    size="small" 
                    placeholder="Matricule" 
                    variant="outlined" 
                    value={dataState.matricule} 
                    onChange={(e) => setDataState(prev => ({ ...prev, matricule: e.target.value }))} 
                    inputProps={{ style: { textTransform: "uppercase" } }} 
                    disabled={isProfessionalDisabled} 
                  />
                </FormRow>
              </FormSection>
            </Grid>

            {/* Contract Information Section */}
            <Grid item xs={12}>
              <FormSection title="Informations contractuelles" sectionKey="contractInfo" linked={isEditing && updateOptions.professionalAndContract}>
                <FormRow label="Date de début de contrat" required error={errors.date_prise} disabled={isContractDisabled}>
                  <TextField 
                    type="date" 
                    fullWidth 
                    variant="outlined" 
                    size="small" 
                    name="date_prise" 
                    value={form.date_prise} 
                    onChange={handleInputChange} 
                    InputLabelProps={{ shrink: true }} 
                    disabled={isContractDisabled} 
                  />
                </FormRow>

                <FormRow label="Date de fin de contrat" required error={errors.date_fin} disabled={isContractDisabled}>
                  <TextField 
                    type="date" 
                    fullWidth 
                    variant="outlined" 
                    size="small" 
                    name="date_fin" 
                    value={form.date_fin} 
                    onChange={handleInputChange} 
                    InputLabelProps={{ shrink: true }} 
                    disabled={isContractDisabled} 
                  />
                </FormRow>

                <FormRow label="Niveau de stage" required disabled={isContractDisabled}>
                  <FormControl fullWidth variant="outlined" size="small" disabled={isContractDisabled}>
                    <InputLabel>Stage</InputLabel>
                    <Select value={form.typestage} onChange={handleStageTypeChange} label="Stage" disabled={isContractDisabled}>
                      {stageOption.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          <div>
                            <div>{option.label}</div>
                            <Typography variant="caption" color="textSecondary">{option.level}</Typography>
                          </div>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FormRow>

                <FormRow label="Indemnité de stage" disabled={isContractDisabled}>
                  <TextField 
                    fullWidth 
                    value={new Intl.NumberFormat('fr-FR').format(form.indemnity)} 
                    variant="outlined" 
                    size="small" 
                    disabled 
                    InputProps={{ startAdornment: <InputAdornment position="start">AR</InputAdornment> }} 
                  />
                </FormRow>
              </FormSection>
            </Grid>

            <Grid item xs={12}>
              <div className={classes.infoText}>
                <Typography variant="body2" color="textSecondary">
                  <RequiredStar /> Champs obligatoires
                </Typography>
              </div>
            </Grid>
          </Grid>
        </Paper>

        {/* Image Upload Dialog */}
        <Dialog open={uiState.opens} onClose={() => setUiState(prev => ({ ...prev, opens: false }))} maxWidth="sm" fullWidth>
          <DialogTitle>Importer une photo</DialogTitle>
          <DialogContent>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Avatar src={uiState.newImage || form.url_name_image} style={{ width: 150, height: 150, margin: '0 auto 20px' }} />
              <DialogContentText>La taille de l'image doit être &lt; 1Mb et le type doit être .jpg, .png, .gif</DialogContentText>
              <input ref={fileInputRef} accept="image/*" type="file" style={{ display: 'none' }} onChange={handleImageChange} />
              <Button variant="outlined" onClick={() => fileInputRef.current?.click()} startIcon={<PhotoCamera />}>Parcourir</Button>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUiState(prev => ({ ...prev, opens: false }))}>Annuler</Button>
            <Button onClick={handleSaveImage} color="primary" variant="contained">Enregistrer</Button>
          </DialogActions>
        </Dialog>

        {/* Crop Dialog */}
        {uiState.cropping && (
          <CropDialog
            imageSrc={uiState.tempImage}
            onCropComplete={(croppedUrl) => {
              fetch(croppedUrl).then(res => res.blob()).then(blob => {
                const file = new File([blob], uiState.croppedFileName || 'cropped-image.jpg', { type: blob.type });
                setUiState(prev => ({ ...prev, newImage: croppedUrl, selectedFile: file, tempImage: null, cropping: false }));
              });
            }}
            onCancel={() => setUiState(prev => ({ ...prev, cropping: false, tempImage: null }))}
          />
        )}
      </div>
    </MainContainer>
  );
}

export default StagiaireAdd;
